import React from 'react'

import {
  withCitrusTheme,
  Box,
  Display,
  FlexDirection,
  JustifyContent,
  AlignItems,
  Color,
  Body1,
  Heading2,
} from '@gousto-internal/citrus-react'

import type { IRAFOffer } from '../ReferralPresentation'

const InstructionItem = withCitrusTheme(Box, (theme) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'top',
  padding: '24px',
  boxShadow: '2px 4px 8px 0px #0000000F',
  flex: 1,
  maxWidth: '33.33%',
  margin: '0',
  background: theme.color(Color.White),
  borderRadius: '16px',
  position: 'relative',
  [`@media (max-width: 886px)`]: {
    maxWidth: '100%',
    width: '100%',
    margin: '18px 0',
    borderRight: 'none',
    ':last-child': {
      borderBottom: 'none',
    },
  },
}))

const StepNumber = withCitrusTheme('span', (theme) => ({
  position: 'absolute',
  top: '-16px',
  left: '10px',
  display: 'inline-block',
  background: theme.color(Color.ColdGrey_500),
  color: theme.color(Color.White),
  borderRadius: '50%',
  width: '2rem',
  height: '2rem',
  textAlign: 'center',
  lineHeight: '2rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  zIndex: 1,
}))

const Container = withCitrusTheme(Box, () => ({
  gap: '24px',
  paddingTop: '40px',
}))

const InstructionsContainer = withCitrusTheme(Box, (_) => ({
  display: 'flex',
  background: undefined,
  borderRadius: '8px',
  width: '100%',
  overflow: 'hidden',
  flexWrap: 'wrap',
  padding: '20px 0px',
  gap: '16px',
  margin: '18px 0px 20px',
  [`@media (max-width: 886px)`]: {
    borderRadius: '0',
    flexDirection: 'column',
    padding: '16px',
  },
}))

const LinkText = withCitrusTheme('a', (theme) => ({
  color: theme.color(Color.Secondary_400),
}))

type RAFInstructionsProps = {
  offer: IRAFOffer
}

export const RAFInstructions: React.FC<RAFInstructionsProps> = ({ offer }) => {
  const firstBoxDiscount = offer.get('firstBoxDiscountFormatted') as string
  const firstMonthDiscount = offer.get('firstMonthDiscountFormatted') as string
  const secondBoxDiscount = offer.has('secondBoxDiscountFormatted')
    ? (offer.get('secondBoxDiscountFormatted') as string)
    : null
  const credit = offer.get('creditFormatted') as string
  const discountDuration = offer.getIn(['discountDuration', 'formatted']) as string

  const copyText = () => {
    if (secondBoxDiscount) {
      return `Invite your friends to try Gousto. We’ll pop ${credit} in your account and your friends
            will get ${firstBoxDiscount} off their first box, ${secondBoxDiscount} off their second
            box and ${firstMonthDiscount} off all other boxes in their first ${discountDuration}.`
    }

    return `Invite your friends to try Gousto. We’ll pop ${credit} in your account and your friends will
            get ${firstBoxDiscount} off their first box and ${firstMonthDiscount} off all other boxes in their first ${discountDuration}.
`
  }

  return (
    <Container>
      <Box
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.Center}
        alignItems={AlignItems.Center}
        gap={10}
      >
        <Heading2 color={Color.ColdGrey_900}>How it works</Heading2>
      </Box>
      <InstructionsContainer display={Display.Flex} justifyContent={JustifyContent.SpaceBetween}>
        <InstructionItem>
          <StepNumber>1</StepNumber>
          <Body1 color={Color.ColdGrey_800}>{copyText()}</Body1>
        </InstructionItem>
        <InstructionItem>
          <StepNumber>2</StepNumber>
          <Body1 color={Color.ColdGrey_800}>
            You can refer up to 20 friends every month. That’s £400 worth of Gousto credit to use on
            recipes and goodies from the Gousto market.
          </Body1>
        </InstructionItem>
        <InstructionItem>
          <StepNumber>3</StepNumber>
          <Body1 color={Color.ColdGrey_800}>
            Your credit shows up once your friend’s first box is delivered. See full T&Cs{' '}
            <LinkText href="https://cook.gousto.co.uk/raf-tcs/" target="_blank" rel="noreferrer">
              here
            </LinkText>
          </Body1>
        </InstructionItem>
      </InstructionsContainer>
    </Container>
  )
}
